import React, { useState, useEffect } from 'react';
import { 
  Phone, 
  MapPin, 
  Calendar, 
  User, 
  Mail, 
  Heart, 
  Award, 
  Clock, 
  BookOpen, 
  Briefcase,
  Globe,
  Building,
  FileText,
  Shield,
  Star,
  X,
  Download,
  Printer,
  Sparkles,
  Zap,
  Target,
  QrCode
} from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { toast } from 'react-hot-toast';

const VolunteerIdCard = ({ volunteer, ngo, isOpen, onClose }) => {
  const [autoClose, setAutoClose] = useState(true);
  const [loadingIdCard, setLoadingIdCard] = useState(false);

  // Debug logging to identify data structure issues
  useEffect(() => {
    if (volunteer) {
      console.log('VolunteerIdCard - Volunteer data received:', volunteer);
      console.log('VolunteerIdCard - Emergency contact type:', typeof volunteer.emergencyContact);
      console.log('VolunteerIdCard - Skills type:', typeof volunteer.skills);
      console.log('VolunteerIdCard - Skills is array:', Array.isArray(volunteer.skills));
    }
  }, [volunteer]);

  // Helper functions
  const getExpiryDate = () => {
    if (volunteer?.joinDate) {
      const date = new Date(volunteer.joinDate);
      const expiryDate = new Date(date);
      expiryDate.setFullYear(expiryDate.getFullYear() + 1);
      return expiryDate.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    }
    return 'Dec 31, 2025';
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return 'N/A';
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const getProfilePictureUrl = (profilePicture) => {
    if (!profilePicture) return '/images/r.png';
    if (profilePicture.startsWith('http')) return profilePicture;
    return `${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:3011'}/${profilePicture}`;
  };

  const getNgoLogoUrl = (logo) => {
    if (!logo) return null;
    if (logo.startsWith('http')) return logo;
    return `${process.env.REACT_APP_API_URL || 'http://localhost:3011'}/uploads/ngos/${logo}`;
  };

  // PDF Generation - captures complete ID card view
  const generatePDF = async () => {
    try {
      // Capture the entire ID card container including additional information
      const element = document.getElementById('id-card-container');
      if (!element) {
        toast.error('ID card container not found');
        return;
      }

      toast.loading('Generating Complete ID Card PDF...', { id: 'pdf-generation' });

      const canvas = await html2canvas(element, {
        scale: 3,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        onclone: (clonedDoc) => {
          // Ensure images are loaded in cloned document
          const clonedElement = clonedDoc.getElementById('id-card-container');
          if (clonedElement) {
            const images = clonedElement.querySelectorAll('img');
            images.forEach(img => {
              if (img.src) {
                img.crossOrigin = 'anonymous';
              }
            });
          }
        }
      });

      const imgData = canvas.toDataURL('image/png');
      
      // Calculate PDF dimensions to fit the entire content
      const imgWidth = 170; // mm - wider to accommodate the layout
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      // Create PDF with appropriate dimensions
      const pdf = new jsPDF({
        orientation: imgHeight > imgWidth ? 'portrait' : 'landscape',
        unit: 'mm',
        format: [imgWidth, imgHeight]
      });
      
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      
      // Generate filename
      const filename = `volunteer-id-${typeof volunteer?.name === 'string' ? volunteer.name.replace(/\s+/g, '-').toLowerCase() : 'card'}.pdf`;
      
      pdf.save(filename);
      
      toast.success('Complete ID Card PDF generated successfully!', { id: 'pdf-generation' });
      
      if (autoClose) {
        setTimeout(() => {
          onClose();
        }, 1000);
      }
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF. Trying print instead...', { id: 'pdf-generation' });
      
      // Fallback to print
      setTimeout(() => {
        handlePrint();
      }, 1000);
    }
  };

  // Print Function
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const element = document.getElementById('id-card-container');
    
    if (!element) {
      toast.error('ID card container not found');
      return;
    }

    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Volunteer ID Card - ${typeof volunteer?.name === 'string' ? volunteer.name : 'N/A'}</title>
          <style>
            @media print {
              body { margin: 0; padding: 0; }
              .id-card-container { 
                width: 100%; 
                margin: 0 auto; 
                page-break-after: always;
                page-break-inside: avoid;
              }
            }
            .id-card-container {
              font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
              width: 100%;
              background: white;
              padding: 20px;
              box-sizing: border-box;
            }
            .id-card {
              width: 85.6mm;
              height: 54mm;
              background: linear-gradient(135deg, #1e293b 0%, #0f172a 50%, #000000 100%);
              border-radius: 16px;
              padding: 16px;
              box-sizing: border-box;
              color: white;
              position: relative;
              overflow: hidden;
              margin: 0 auto 20px;
            }
            .additional-info {
              background: linear-gradient(to right, #f9fafb, #f3f4f6);
              border-radius: 16px;
              padding: 20px;
              border: 1px solid #e5e7eb;
            }
          </style>
        </head>
        <body>
          ${element.outerHTML}
        </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);

    if (autoClose) {
      setTimeout(() => {
        onClose();
      }, 1000);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto border border-gray-100">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800">Volunteer ID Card</h2>
              <p className="text-sm text-gray-500">Professional 2025 Design</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-all duration-200"
          >
            <X size={20} />
          </button>
        </div>

        {/* ID Card Container */}
        <div className="p-6">
          {/* Container for PDF capture - includes both ID card and additional info */}
          <div id="id-card-container">
            {/* Professional ID Card Design */}
            <div 
              id="volunteer-id-card"
              className="w-full max-w-sm mx-auto relative overflow-hidden rounded-2xl shadow-2xl"
              style={{ aspectRatio: '0.625' }}
            >
              {/* Dark background with modern gradient */}
              <div className="absolute inset-0 bg-gradient-to-br from-red-900 via-rose-900 to-black"></div>
              
              {/* Professional geometric background elements */}
              <div className="absolute inset-0 overflow-hidden">
                {/* Top accent - more subtle */}
                <div className="absolute -top-6 -right-6 w-24 h-24 bg-gradient-to-br from-red-700/70 to-rose-800/70 rounded-full blur-sm"></div>
                <div className="absolute top-8 right-12 w-6 h-6 bg-red-400/60 rounded-full"></div>
                <div className="absolute top-16 right-6 w-3 h-3 bg-red-300/50 rounded-full"></div>
                
                {/* Bottom accent - more professional */}
                <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-gradient-to-tr from-red-800/50 to-rose-900/50 rounded-full blur-sm"></div>
                <div className="absolute bottom-12 left-6 w-4 h-4 bg-red-500/40 rounded-full"></div>
                
                {/* Side professional accents */}
                <div className="absolute top-1/4 -left-2 w-8 h-8 bg-gradient-to-r from-red-600/60 to-rose-700/60 rounded-full blur-sm"></div>
                <div className="absolute bottom-1/3 -right-1 w-6 h-6 bg-gradient-to-l from-red-500/50 to-rose-600/50 rounded-full blur-sm"></div>
              </div>

              {/* ID Card Content */}
              <div className="relative p-5 text-white h-full flex flex-col">
                {/* Header with logo and company name */}
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center space-x-2">
                    {ngo?.logo ? (
                      <img
                        src={getNgoLogoUrl(ngo.logo)}
                        alt="NGO Logo"
                        className="w-8 h-8 rounded-lg"
                        onError={(e) => {
                          if (e.target && e.target.style) {
                            e.target.style.display = 'none';
                          }
                        }}
                      />
                    ) : (
                      <div className="w-8 h-8 bg-gradient-to-br from-red-600 to-red-700 rounded-lg flex items-center justify-center">
                        <Building className="w-4 h-4 text-white" />
                      </div>
                    )}
                    <div className="flex flex-col">
                      <span className="text-xs text-white/60 font-medium tracking-wide">POWERED BY NGO</span>
                      <span className="text-white font-bold text-sm tracking-wide">{ngo?.name || 'NGO Name'}</span>
                    </div>
                  </div>
                </div>

                {/* Main ID Card Section */}
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    {/* Profile Picture */}
                    <div className="w-32 h-32 rounded-2xl overflow-hidden border-4 border-white/30 bg-white/10 backdrop-blur-sm shadow-lg mx-auto mb-4">
                      {volunteer?.profilePicture ? (
                        <img
                          src={getProfilePictureUrl(volunteer.profilePicture)}
                          alt="Volunteer"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            if (e.target && e.target.style) {
                              e.target.style.display = 'none';
                            }
                          }}
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-red-400 to-red-500 flex items-center justify-center">
                          <User className="w-16 h-16 text-white/90" />
                        </div>
                      )}
                    </div>

                    {/* Name */}
                    <h2 className="text-xl font-bold text-white mb-2">
                      {volunteer?.name ? volunteer.name.toUpperCase() : 'VOLUNTEER NAME'}
                    </h2>
                    
                    {/* Skills Display */}
                    <div className="text-center mb-4">
                      {volunteer?.skills && Array.isArray(volunteer.skills) && volunteer.skills.length > 0 ? (
                        <div className="flex flex-wrap justify-center gap-1">
                          {volunteer.skills.slice(0, 3).map((skill, index) => (
                            <span 
                              key={index}
                              className="text-red-300 text-xs font-medium px-2 py-1 bg-white/10 rounded-full"
                            >
                              {skill}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="text-red-300 font-semibold text-base">VOLUNTEER</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* ID Card Details */}
                <div className="space-y-3">
                  {/* ID Number */}
                  <div className="text-center">
                    <div className="bg-white/15 backdrop-blur-sm px-4 py-2 rounded-lg inline-block">
                      <span className="text-white/80 text-sm font-medium mr-2">ID NO.</span>
                      <span className="text-red-300 font-mono text-sm font-bold">
                        {volunteer?._id?.slice(-8) || '125-456-7850'}
                      </span>
                    </div>
                  </div>

                  {/* Contact Information & Key Details - Equal Spacing */}
                  <div className="space-y-2.5">
                    <div className="flex justify-between items-center">
                      <span className="text-white/80 text-sm font-medium">DOB:</span>
                      <span className="text-white text-sm">
                        {volunteer?.formattedDateOfBirth || volunteer?.dateOfBirth ? new Date(volunteer.dateOfBirth).toLocaleDateString('en-GB') : 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-white/80 text-sm font-medium">E-MAIL:</span>
                      <span className="text-white text-xs">
                        {volunteer?.email || 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-white/80 text-sm font-medium">PHONE:</span>
                      <span className="text-red-300 font-mono text-sm">
                        {volunteer?.phone || 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-white/80 text-sm font-medium">ADDRESS:</span>
                      <span className="text-white text-xs text-right">
                        {volunteer?.address || 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-white/80 text-sm font-medium">CITY:</span>
                      <span className="text-white text-sm">
                        {volunteer?.city || 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-white/80 text-sm font-medium">STATE:</span>
                      <span className="text-white text-sm">
                        {volunteer?.state || 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-white/80 text-sm font-medium">EXPIRES:</span>
                      <span className="text-red-300 text-sm font-semibold">
                        {getExpiryDate()}
                      </span>
                    </div>
                  </div>

                  {/* Barcode */}
                  <div className="flex justify-center mb-3">
                    <div className="flex items-center space-x-px">
                      {[...Array(15)].map((_, i) => (
                        <div 
                          key={i} 
                          className="bg-red-600 opacity-90"
                          style={{
                            width: Math.random() > 0.5 ? '2px' : '1px',
                            height: '20px'
                          }}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Website */}
                  <div className="text-center">
                    <div className="text-xs text-white/70 font-medium tracking-wider">
                      www.thebloodhero.com
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Information Panel with modern design - REMOVED */}
          </div> {/* End of id-card-container */}
        </div>

        {/* Action Buttons with modern styling */}
        <div className="p-6 border-t border-gray-100 bg-gradient-to-r from-gray-50 to-gray-100">
          <div className="flex items-center justify-between mb-4">
            <label className="flex items-center space-x-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={autoClose}
                onChange={(e) => setAutoClose(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span>Auto-close after action</span>
            </label>
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={generatePDF}
              disabled={loadingIdCard}
              className="flex-1 bg-gradient-to-r from-red-600 to-red-700 text-white px-4 py-3 rounded-xl hover:from-red-700 hover:to-red-800 transition-all duration-200 flex items-center justify-center space-x-2 font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              <Download size={18} />
              <span>Download Complete PDF</span>
            </button>
            <button
              onClick={handlePrint}
              className="flex-1 bg-gradient-to-r from-rose-600 to-rose-700 text-white px-4 py-3 rounded-xl hover:from-rose-700 hover:to-rose-800 transition-all duration-200 flex items-center justify-center space-x-2 font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              <Printer size={18} />
              <span>Print</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VolunteerIdCard;
